import React, { useState, useEffect, useContext } from "react";
import {
  Segment,
  Label,
  Input,
  Icon,
  Popup,
  Button,
  Confirm,
} from "semantic-ui-react";
import { SlidesContextEdit } from "../../../../../../../contexts/MediaContext";
import { CustomQuillEditor } from "../../../../../../Shared";
import "../TemplateManager.scss";

export const Smu01 = React.memo((props) => {
  const { index, slideId, onRemoveSlideByIndex } = props;
  const { slidesActiveSessionEdit } = useContext(SlidesContextEdit);
  const [showConfirm, setShowConfirm] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  /**
   * Updates the `title` and `content` states based on the current slide's information.
   *
   * This `useEffect` hook is triggered whenever the `slideId` changes. It searches the `slidesActiveSessionEdit`
   * array for a slide that matches the `slideId`. If such a slide is found and it contains content, the hook
   * updates the `title` and `content` states with the title and content of the found slide, respectively.
   *
   * This ensures that the displayed title and content are always in sync with the current slide, allowing for
   * accurate editing and presentation of slide information.
   *
   * @dependency slideId - The ID of the current slide, changes to which trigger the hook.
   */
  useEffect(() => {
    const slide = slidesActiveSessionEdit.find(
      (slide) => slide._id === slideId
    );
    if (slide && slide.content) {
      setTitle(slide.content.title);
      setContent(slide.content.content);
    }
  }, [slideId]);

  /**
   * Updates the title and content of a slide within the `slidesActiveSessionEdit` array.
   *
   * This `useEffect` hook is executed when either the `title` or `content` state changes. It searches for the slide
   * with the matching `slideId` in the `slidesActiveSessionEdit` array. If the slide is found (indicated by `slideIndex`
   * not being -1), the hook updates the `title` and `content` of the slide's content object with the new values.
   *
   * This operation ensures that changes made to the title or content of the slide in the component's state are
   * reflected in the `slidesActiveSessionEdit` array, keeping the session's slide data up-to-date with the user's
   * edits.
   *
   * @dependency title - The current title value to be updated for the slide.
   * @dependency content - The current content value to be updated for the slide.
   */
  useEffect(() => {
    const slideIndex = slidesActiveSessionEdit.findIndex(
      (slide) => slide._id === slideId
    );
    if (slideIndex !== -1) {
      slidesActiveSessionEdit[slideIndex].content.title = title;
      slidesActiveSessionEdit[slideIndex].content.content = content;
    }
  }, [title, content]);

  /**
   * Event handlers for updating the title and content of a slide.
   *
   * `handleTitleChange`:
   * - Triggered by changes in the title input field.
   * - Updates the `title` state with the trimmed input value, removing leading whitespace.
   *
   * `handleContentChange`:
   * - Triggered by changes in the content input area.
   * - Directly updates the `content` state with the provided value without trimming.
   *
   * These functions ensure that the slide's title and content are updated in response to user inputs, maintaining the integrity of the slide's information.
   */
  const handleTitleChange = (event) => {
    setTitle(event.target.value.trimStart());
  };

  const handleContentChange = (content) => {
    setContent(content);
  };

  return (
    <>
      <Segment raised className="media-slide">
        <div className="media-slide__header">
          <div className="media-slide__header">
            <Label attached="top left">
              Hoja {index + 1} de {slidesActiveSessionEdit.length}
            </Label>
          </div>
          <div className="media-slide__button">
            <Popup
              content="Eliminar hoja"
              trigger={
                <Button
                  compact
                  color="grey"
                  icon="trash alternate outline"
                  onClick={onOpenCloseConfirm}
                />
              }
            />
          </div>
        </div>
        <div className="media-slide__input">
          <div className="media-slide__label-div">
            <Icon name="bookmark" />
            <h5 className="media-slide__label">Título:</h5>
          </div>
          <Input
            fluid
            className="media-slide__input-title"
            name="title"
            value={title}
            onChange={handleTitleChange}
          />
        </div>
        <div className="row">
          <div className="media-slide__label-div">
            <Icon name="content" />
            <h5 className="media-slide__label">Contenido:</h5>
          </div>
          <CustomQuillEditor
            className="media-slide__input-content"
            value={content}
            onChange={handleContentChange}
          />
        </div>
      </Segment>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={() => onRemoveSlideByIndex(index)}
        content={`¿Deseas eliminar esta hoja?`}
        size="mini"
      />
    </>
  );
});
